import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "315",
  "slug": "largest-islands-in-the-world-ranked",
  "title": "The 10 Largest Islands in the World, Ranked (And Why Australia Doesn't Count)",
  "excerpt": "Greenland is the largest island on Earth — and the gap between first and second place is enormous. Here are the ten largest islands ranked by area, with the geography, geology, and surprises behind each one.",
  "tldr": "The ten largest islands by area are Greenland (2.16 million km²), New Guinea, Borneo, Madagascar, Baffin, Sumatra, Honshu, Victoria, Great Britain, and Ellesmere. Greenland is more than twice the size of New Guinea. Australia is excluded because at 7.7 million km² it qualifies as a continent rather than an island.",
  "date": "2026-05-05",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800&q=80",
  "readTime": "7 min",
  "content": [
    {
      "type": "paragraph",
      "text": "Most people, asked to name the largest island in the world, will guess Greenland — and they are right. But the gap between Greenland and the runner-up is so large that it dominates the top of the list, and the rest of the rankings contain more surprises than expected. Some of the islands you think are huge are mid-pack at best. Some you have probably never heard of are in the top ten. And one famously enormous landmass — Australia — does not count as an island at all."
    },
    {
      "type": "paragraph",
      "text": "Here is the definitive list of the ten largest islands on Earth, ranked by area, with the geography and geology behind each one."
    },
    {
      "type": "heading",
      "text": "Why Australia Is Not on This List"
    },
    {
      "type": "paragraph",
      "text": "Australia covers 7.7 million square kilometres of land surrounded by ocean — so why is it not the largest island in the world? Because the convention is that Australia is classified as a continent, not an island, and the two categories are treated as mutually exclusive. The size threshold for continental status is debated, but Australia's 7.7 million square kilometres is roughly three times the size of Greenland, and the geological argument is that Australia sits on its own tectonic plate with its own continental crust. If you ignored that convention and treated everything as an island, Australia would be number one and Greenland would be a distant number two."
    },
    {
      "type": "paragraph",
      "text": "With that caveat handled, here is the ranked list of true islands."
    },
    {
      "type": "heading",
      "text": "1. Greenland — 2,166,086 km²"
    },
    {
      "type": "paragraph",
      "text": "Greenland is the largest island on Earth, and it is not even close. It is roughly the size of Saudi Arabia, more than three times the size of Texas, and about 75 percent of the size of the entire Indian subcontinent. About 80 percent of its surface is covered by the Greenland Ice Sheet, the world's second-largest body of ice after Antarctica. The ice sheet alone holds enough water to raise global sea levels by approximately 7.4 metres if it melted entirely."
    },
    {
      "type": "paragraph",
      "text": "Despite its size, Greenland has fewer than 57,000 inhabitants, almost all of whom live in narrow coastal settlements ringed by mountains and fjords. The interior is essentially uninhabitable. Politically, Greenland is an autonomous territory within the Kingdom of Denmark, with its own parliament and increasing moves toward full independence."
    },
    {
      "type": "heading",
      "text": "2. New Guinea — 785,753 km²"
    },
    {
      "type": "paragraph",
      "text": "New Guinea is the second-largest island and one of the most biologically and culturally diverse places on Earth. It is split between two countries: Papua New Guinea in the east and the Indonesian provinces of Papua and West Papua in the west. The island contains an estimated 5 percent of the world's biodiversity in less than 0.5 percent of its land area, and is home to over 1,000 distinct languages — roughly one sixth of the languages spoken on the planet."
    },
    {
      "type": "paragraph",
      "text": "Geographically, New Guinea is dominated by a central mountain range with peaks above 4,800 metres, surrounded by some of the most extensive tropical rainforest on the planet. From orbit, it appears as a dark green, mountain-spined wedge between the equator and Australia."
    },
    {
      "type": "heading",
      "text": "3. Borneo — 748,168 km²"
    },
    {
      "type": "paragraph",
      "text": "Borneo is the third-largest island and the only one shared by three countries: Indonesia (which holds about 73 percent in the provinces collectively known as Kalimantan), Malaysia (which holds about 26 percent in Sabah and Sarawak), and Brunei (which occupies a small enclave on the northwest coast). It is also one of the world's most ecologically significant landmasses, home to ancient rainforest, the Bornean orangutan, and one of the planet's most diverse arrays of plant and animal species."
    },
    {
      "type": "paragraph",
      "text": "From satellite imagery, Borneo has changed dramatically over the past 30 years. Vast swathes of the lowland rainforest have been cleared for oil palm plantations, which produce the regular grid patterns you see on the southern half of the island today. The mountainous interior — particularly the central Mueller Range and the Heart of Borneo — remains heavily forested."
    },
    {
      "type": "heading",
      "text": "4. Madagascar — 587,041 km²"
    },
    {
      "type": "paragraph",
      "text": "Madagascar sits in the Indian Ocean about 400 kilometres off the southeastern coast of Africa, but it has been separated from any other landmass for so long that roughly 90 percent of its wildlife is endemic — found nowhere else on Earth. The country is dominated by a central highland spine, with the wettest forests along the eastern escarpment, a dry savanna and spiny forest on the west, and the iconic baobab-studded plains in the southwest."
    },
    {
      "type": "paragraph",
      "text": "From orbit, Madagascar's deforestation history is starkly visible: the red colour of exposed lateritic soil, particularly along eroded valley floors, has earned it the nickname \"the great red island.\" The eastern rainforests appear as dark green strips along the coast, while the central highlands are a patchwork of rice terraces and grazing land."
    },
    {
      "type": "heading",
      "text": "5. Baffin Island — 507,451 km²"
    },
    {
      "type": "paragraph",
      "text": "Baffin Island, in the Canadian Arctic, is the largest island in Canada and the fifth-largest in the world. It is roughly the size of California and Nevada combined, but has a population of just 13,000 people — almost all Inuit, concentrated in a handful of small communities. The island is dominated by snow-capped mountains, fjords cut by glaciers, and the imposing Penny Ice Cap on the central plateau."
    },
    {
      "type": "heading",
      "text": "6. Sumatra — 443,066 km²"
    },
    {
      "type": "paragraph",
      "text": "Sumatra is the largest island that lies entirely within one country (Indonesia). It is bisected lengthwise by the Barisan Mountains, an active volcanic chain that includes Lake Toba, the world's largest volcanic crater lake and the site of one of the most catastrophic eruptions in geological history about 74,000 years ago."
    },
    {
      "type": "paragraph",
      "text": "The eastern half of Sumatra is mostly low-lying swamp and rainforest converted to oil palm and rubber plantations. The western half is mountainous, more rugged, and contains some of Indonesia's most pristine remaining rainforest in places like Bukit Barisan Selatan and Kerinci Seblat national parks."
    },
    {
      "type": "heading",
      "text": "7. Honshu — 225,800 km²"
    },
    {
      "type": "paragraph",
      "text": "Honshu is the largest of Japan's main islands and one of the most densely populated landmasses on Earth. It contains Tokyo (the world's largest metropolitan area), most of Japan's industrial heartland, and over 100 million people. From orbit, Honshu is unmistakable — narrow, mountainous, and crowded with cities and rice paddies pressed into every available coastal plain."
    },
    {
      "type": "heading",
      "text": "8. Victoria Island — 217,291 km²"
    },
    {
      "type": "paragraph",
      "text": "Victoria Island, in the Canadian Arctic, is the eighth-largest island in the world. It straddles the boundary between Nunavut and the Northwest Territories. With a population of only around 1,800 people, it is one of the most sparsely populated large landmasses on Earth. The terrain is mostly low, lake-strewn tundra, and the island lies almost entirely above the Arctic Circle."
    },
    {
      "type": "heading",
      "text": "9. Great Britain — 209,331 km²"
    },
    {
      "type": "paragraph",
      "text": "Great Britain — the island containing England, Scotland, and Wales — is the largest island in Europe and the ninth-largest in the world. It is also the third-most-populous island on Earth, after Java and Honshu, with around 67 million inhabitants. Great Britain is geographically diverse for its size: rolling lowlands in the south and east, hill country in the west, and high mountainous terrain in northern Scotland."
    },
    {
      "type": "heading",
      "text": "10. Ellesmere Island — 196,236 km²"
    },
    {
      "type": "paragraph",
      "text": "Ellesmere Island is the third-largest island in Canada and the most northern major landmass on Earth — its northernmost point, Cape Columbia, sits just 760 kilometres from the geographic North Pole. It is dominated by mountains, ice caps, and fjords, with a population of fewer than 150 people. Ellesmere is one of the few places on Earth where you can still see actively retreating glaciers calving directly into the sea from any reasonable vantage point."
    },
    {
      "type": "heading",
      "text": "What the Rankings Reveal"
    },
    {
      "type": "paragraph",
      "text": "A few things jump out from this list. First, the high latitudes dominate: four of the top ten islands (Greenland, Baffin, Victoria, Ellesmere) are in the Arctic, and almost no one lives on three of them. Second, the tropics also feature heavily: New Guinea, Borneo, Madagascar, and Sumatra together contain enormous biodiversity. Third, the only European entry is Great Britain at number nine — Europe simply does not have many large islands. And fourth, the gap between Greenland and number two is so large that even combining New Guinea and Borneo barely matches Greenland's area."
    }
  ]
};
